import { IMeasurement, ISensorDataSeries, ITemporalValueWithUnit } from 'app/business-logic/domain-models/Monitoring';
import { IWeatherReport, IWeatherStation, IWeatherStationSensor } from 'app/business-logic/domain-models/Weather';
import { getSpotDataForSensors } from 'app/business-logic/services/sensor-data/SensorDataManager';
import { hasLengthAtLeast } from 'app/helpers/hasLengthAtLeast';
import Guid from 'core/types/Guid';
import { DateTime } from 'luxon';

import { fetchSensorDataSeries } from './fetchSensorData';
import { AggregateType } from 'app/__generated__/global';

const FALLBACK_PRECIPITATION_DATA = [
  {
    sensorId: null,
    data: [],
    staleDataTimeoutSeconds: 0,
  },
];

interface ITemporalWeatherSensors {
  humiditySensorId?: Guid;
  temperatureSensorId?: Guid;
  windDirectionSensorId?: Guid;
  windSpeedSensorId?: Guid;
}

export async function fetchWeather(weatherStation: IWeatherStation, timeMillis: number) {
  const temporalSensors = getTemporalSensors(weatherStation);
  const temporalSensorIds = (Object.values(temporalSensors) as Guid[]).filter(id => !!id);
  const precipitationSensorId = weatherStation.precipitationSensor?.id;
  const canFetchDataSeries = !!(precipitationSensorId && timeMillis);
  const currentTime = DateTime.fromMillis(timeMillis);
  const earliestStart = currentTime.startOf('day').set({ hour: 9 });
  const rainfallStart = earliestStart.toMillis() >= timeMillis ? earliestStart.minus({ days: 1 }) : earliestStart;
  const [precipitationData] = canFetchDataSeries
    ? await fetchSensorDataSeries([precipitationSensorId], rainfallStart, currentTime, {
        type: AggregateType.Sum,
      })
    : (FALLBACK_PRECIPITATION_DATA as (ISensorDataSeries | undefined)[]);

  const sensorData = await getSpotDataForSensors(temporalSensorIds, timeMillis);

  if (sensorData === null) console.error(`getSpotDataForSensors failed`);

  return createWeatherReport(weatherStation, temporalSensors, sensorData, precipitationData);
}

function getTemporalSensors(weatherStation: IWeatherStation): ITemporalWeatherSensors {
  const { humiditySensor, temperatureSensor, windDirectionSensor, windSpeedSensor } = weatherStation;

  return {
    humiditySensorId: humiditySensor?.id,
    temperatureSensorId: temperatureSensor?.id,
    windDirectionSensorId: windDirectionSensor?.id,
    windSpeedSensorId: windSpeedSensor?.id,
  };
}

function createWeatherReport(
  weatherStation: IWeatherStation,
  temporalSensors: ITemporalWeatherSensors,
  sensorData: IMeasurement[] | null,
  rainfallData: ISensorDataSeries | undefined
) {
  const weatherReport: IWeatherReport = {
    timeMillis: null,
    weatherStationId: weatherStation.monitorId,
    weatherStationName: weatherStation.monitorName,

    humidity: null,
    temperature: null,
    windDirection: null,
    windSpeed: null,
    rainSince9AM: null,
  };

  if (!sensorData) return weatherReport;

  sensorData.forEach(dataPoint => {
    if (!dataPoint) return;

    switch (dataPoint.sensorId) {
      case temporalSensors.humiditySensorId:
        weatherReport.humidity = createTemporalValueWithUnit(weatherStation.humiditySensor, dataPoint);
        break;

      case temporalSensors.temperatureSensorId:
        weatherReport.temperature = createTemporalValueWithUnit(weatherStation.temperatureSensor, dataPoint);
        break;

      case temporalSensors.windDirectionSensorId:
        weatherReport.windDirection = createTemporalValueWithUnit(weatherStation.windDirectionSensor, dataPoint);
        break;

      case temporalSensors.windSpeedSensorId:
        weatherReport.windSpeed = createTemporalValueWithUnit(weatherStation.windSpeedSensor, dataPoint);
        break;
    }
  });

  if (rainfallData?.data.length) {
    if (hasLengthAtLeast(rainfallData.data, 1)) {
      const [[timeMillis, value]] = rainfallData.data;
      weatherReport.rainSince9AM = createTemporalValueWithUnit(weatherStation.precipitationSensor, {
        timeMillis,
        value: value ?? null,
      });
    }
  }

  const dataPointTimes = [...sensorData]
    .flatMap(dataPoint => {
      if (!dataPoint) return [];
      return dataPoint.timeMillis;
    })
    .concat(
      rainfallData?.data.flatMap(([timeMillis]) => {
        if (!timeMillis) return [];
        return timeMillis;
      }) ?? []
    );

  weatherReport.timeMillis = Math.max.apply(null, dataPointTimes);

  return weatherReport;
}

function createTemporalValueWithUnit(
  sensor: IWeatherStationSensor | null,
  dataPoint: Pick<IMeasurement, 'timeMillis' | 'value'>
): ITemporalValueWithUnit {
  const { unit } = sensor ?? { unit: null };
  const { timeMillis, value } = dataPoint;

  return {
    timeMillis,
    value,
    unit,
  };
}
